<template>
  <BasePage :back-button="true" :pageTitle=lessonName show-menu>
    <!-- Loading State (including PWA background recovery) -->
    <LoadingSpinner v-if="loading || isRecovering" />

    <!-- Main Content -->
    <div v-else-if="isReady" class="d-flex flex-column" style="min-height: calc(100vh - 100px)">
      <!-- Progress Bar -->
      <div class="mb-3">
        <ProgressBar
            :percent="(lessonTicketData.registered/lessonTicketData.total)*100"
            :total="lessonTicketData.total"
            :raw="lessonTicketData.registered"
            heading="Registered"
        />
        <PaxAllocation class="mt-2" accordion show-count :title="`Registered Lesson Participants`" :inhabitants="registeredPax"></PaxAllocation>
        <PaxAllocation class="mt-2" accordion show-count :title="`Remaining Lesson Participants`" :inhabitants="unregisteredPax"></PaxAllocation>
      </div>

      <!-- Scanner Component -->
      <QrScannerModal
          v-if="showingScanner"
          :model-value="showingScanner"
          @close="resetState"
          @code-scanned="(code, scan) => loadPassenger('scan', code, scan)"
          @invalid-code="handleInvalidCode"
      />

      <!-- Name Search Modal -->
      <NameSearchModal
          :is-open="showingNameSearch"
          :tickets="lessonTicketData?.tickets || []"
          :heading="`Find ${__DICT__.passenger}`"
          @close="closeNameSearch"
          @select="loadPassenger('manual', $event)"
          showRegisteredStatus
          liftPassStatusText="Registered?"
      />

      <!-- Action Buttons -->
      <div class="fixed-bottom fixed-bottom-bar p-3">
        <!-- Initial State Buttons -->
        <template v-if="currentState === 'initial' && !showingScanner">
          <button class="btn btn-primary w-100 mb-3" @click="startScanner">
            {{ __DICT__.scanBarcode }}
          </button>
          <button class="btn btn-secondary w-100" @click="startNameSearch">
            {{ __DICT__.enterByName }}
          </button>
        </template>

        <!-- Scanner Active State Button -->
        <template v-if="showingScanner">
          <button class="btn btn-secondary w-100" @click="resetState">
            {{ __DICT__.cancel }}
          </button>
        </template>
      </div>
    </div>

    <!-- Success Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showSuccessModal }" tabindex="-1" style="display: block;" v-if="showSuccessModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-success">{{ __DICT__.success }}</div>
            <div>{{ __DICT__.passengerRegistered }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-success w-100" @click="acknowledgeSuccess">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Modal -->
    <div class="modal modal-blur fade" :class="{ 'show': showErrorModal }" tabindex="-1" style="display: block;" v-if="showErrorModal">
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body">
            <div class="modal-title text-danger">{{ __DICT__.error }}</div>
            <div>{{ error?.message || 'An error occurred' }}</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-danger w-100" @click="acknowledgeError">
              {{ __DICT__.ok }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade" :class="{ 'show': showSuccessModal || showErrorModal }" v-if="showSuccessModal || showErrorModal"></div>
  </BasePage>
</template>

<script setup>
import {useWeekStore} from "@/stores/week.js";
import BasePage from "@/components/BasePage.vue";
import { LoadingSpinner } from "@/components/atomic";
import {useRoute} from "vue-router";
import QrScanner from "@/components/QrScanner.vue";
import {ref, watch, onMounted, computed, onUnmounted} from "vue";
import {rememberTicketBookings} from '@/services/qrKeyCache.js';
import {api} from "@/services/api.js";
import ProgressBar from "@/components/ProgressBar.vue";
import NameSearchModal from "@/components/NameSearchModal.vue";
import PaxAllocation from "@/components/PaxAllocation.vue";
import QrScannerModal from "@/components/QrScannerModal.vue";
import {qrRequestCode, qrRequestFields} from "@/utils/qrVerify.js";
import {useEnsureWeekData} from "@/composables/useEnsureWeekData.js";
import {useHaptics} from "@/composables/useHaptics.js";
import {useScanRedeem} from "@/composables/useScanRedeem.js";

// Route and Store
const route = useRoute();
const weekStore = useWeekStore();

// Ensure week data is available (handles PWA background recovery)
const { isRecovering, isReady } = useEnsureWeekData();
const { errorFeedback } = useHaptics();

// State Management
const loading = ref(true);
const lessonTicketData = ref({});
// The QR check can bind a scan to its ticket's booking (qr-binding-ticket-booking).
watch(() => lessonTicketData.value?.tickets, rememberTicketBookings);
const {processing, error, showSuccessModal, showErrorModal, redeem} = useScanRedeem({
  refresh: () => refreshData()
});
const entryMethod = ref(null);
const currentState = ref('initial');

const lessonId = computed( () =>
   route.params.lessonId
)

const lessonName = computed( () => {
  if(lessonTicketData.value.tickets) {
    return lessonTicketData.value.tickets[0].lessonId
  }  //return "Lesson"
})

const registeredPax =computed(() => {
  return lessonTicketData.value.tickets.filter(t => t.registered === 1)
})

const unregisteredPax =computed(() => {
  return lessonTicketData.value.tickets.filter(t => t.registered === 0)
})

// Computed Properties
const showingScanner = computed(() =>
    currentState.value === 'initial' && entryMethod.value === 'scan'
);

const showingNameSearch = computed(() =>
    currentState.value === 'initial' && entryMethod.value === 'manual'
);

// Methods
const generateGuid = () => {
  const s4 = () => Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
};

// scan: the scanner's { raw, token, verification } - absent for name search.
const loadPassenger = async (method, data, scan = null) => {
  if (processing.value) return;

  processing.value = true;
  error.value = null;

  // Guard against null repObj (can happen when PWA resumes from background)
  if (!weekStore.repObj) {
    error.value = { message: 'Session data not available. Please go back and try again.' };
    showErrorModal.value = true;
    processing.value = false;
    return;
  }

  try {
    const payload = {
      weekID: weekStore.weekId,
      barcode: qrRequestCode(scan, data),
      ...qrRequestFields(scan, data),
      handsetTimestamp: new Date().toISOString(),
      idempotentId: generateGuid(),
      repID: weekStore.repObj.repId,
      repName: weekStore.repObj.name,
      scanned: method === 'scan' ? 1 : 0,
      lessonId: lessonId.value,
    };

    await redeem('/api/v3/scan/lesson', payload);
  } catch (e) {
    console.error(e)
    error.value = e;
    resetState();
    showErrorModal.value = true;
    errorFeedback();
  } finally {
    processing.value = false;
    resetState();
  }
};

const startScanner = () => {
  entryMethod.value = 'scan';
};

const startNameSearch = () => {
  entryMethod.value = 'manual';
};

const closeNameSearch = () => {
  resetState();
};

const resetState = () => {
  entryMethod.value = null;
  processing.value = false;
  currentState.value = 'initial';
};

const acknowledgeSuccess = () => {
  showSuccessModal.value = false;
};

const acknowledgeError = () => {
  showErrorModal.value = false;
  error.value = null;
};

const handleInvalidCode = () => {
  error.value = {message: 'Invalid barcode format'};
  showErrorModal.value = true;
  errorFeedback();
};

let refreshTimeout = null;
const refreshData = async () => {
  lessonTicketData.value = await api.get(`/api/workshopLessons/v2/allBarcodes/${route.params.lessonId}`);
};

const refreshDataHook = () => {
  refreshData();
  refreshTimeout = setTimeout(refreshDataHook, 10000);
};


onUnmounted(() => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }
});


// Initial Data Load
onMounted(async () => {
  try {
    await refreshData();
    refreshDataHook();
  } finally {
    loading.value = false;
  }
});
</script>
